import React from 'react';
import { motion } from 'framer-motion';
import { EnhancedHeadline } from './EnhancedHeadline';
import { AnimatedButton } from '@/components/ui/animated-button';
import { TrustIndicator } from '@/components/marketing/TrustIndicator';
import { EmailCaptureForm } from './EmailCaptureForm';
import { OnboardingFlow } from './OnboardingFlow';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { trackCTAClick, conversionTracker, CTA_TESTS } from '@/utils/conversion-tracking';
import { CTA_MESSAGES } from '@/lib/cta-config';
import {
  slideUp,
  staggerItem,
  HERO_ANIMATION_SEQUENCE,
} from '@/lib/animations';

interface AnimatedHeroProps {
  onGetStarted?: () => void;
  onWatchDemo?: () => void;
  showOnboardingFlow?: boolean;
}

export const AnimatedHero: React.FC<AnimatedHeroProps> = ({
  onGetStarted,
  onWatchDemo,
  showOnboardingFlow = false,
}) => {
  const prefersReducedMotion = useReducedMotion();
  
  // A/B Testing for CTA buttons
  const primaryCTAVariant = conversionTracker.getVariant(CTA_TESTS.HERO_PRIMARY_BUTTON.id);
  const secondaryCTAVariant = conversionTracker.getVariant(CTA_TESTS.HERO_SECONDARY_BUTTON.id);
  
  const getPrimaryCTAText = () => {
    switch (primaryCTAVariant) {
      case 'variant_a': return CTA_MESSAGES.primary;
      case 'variant_b': return CTA_MESSAGES.primary;
      default: return CTA_MESSAGES.primary;
    }
  };
  
  const getSecondaryCTAText = () => {
    switch (secondaryCTAVariant) {
      case 'variant_a': return CTA_MESSAGES.demo;
      case 'variant_b': return 'View Demo';
      default: return 'See Live Demo';
    }
  };
  
  const handleGetStartedClick = () => {
    trackCTAClick(getPrimaryCTAText(), 'primary', 'hero', {
      ab_test_variant: primaryCTAVariant,
      has_onboarding_flow: showOnboardingFlow,
    });
    onGetStarted?.();
  };
  
  const handleWatchDemoClick = () => {
    trackCTAClick(getSecondaryCTAText(), 'secondary', 'hero', {
      ab_test_variant: secondaryCTAVariant,
    });
    onWatchDemo?.();
  };

  // Container for the entire hero section
  const heroContainerVariants = {
    initial: {},
    animate: {
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  // Subtitle animation
  const subtitleVariants = {
    initial: slideUp.initial,
    animate: {
      ...slideUp.animate,
      transition: {
        ...slideUp.animate.transition,
        delay: HERO_ANIMATION_SEQUENCE.subtext.delay,
        duration: HERO_ANIMATION_SEQUENCE.subtext.duration,
      },
    },
  };

  // Trust indicators container
  const trustIndicatorsVariants = {
    initial: {},
    animate: {
      transition: {
        staggerChildren: 0.1,
        delayChildren: HERO_ANIMATION_SEQUENCE.trustIndicators.delay,
      },
    },
  };

  const ContainerComponent = prefersReducedMotion ? 'div' : motion.div;
  const containerProps = prefersReducedMotion
    ? {}
    : {
        variants: heroContainerVariants,
        initial: 'initial',
        animate: 'animate',
      };

  return (
    <ContainerComponent
      className='text-center lg:text-left'
      {...containerProps}
    >
      {/* Enhanced Animated Headline with Typing Effect */}
      <div className="relative">
        <EnhancedHeadline className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-black leading-none mb-4 md:mb-6 tracking-tight" />
      </div>

      {/* Enhanced Value Proposition */}
      {prefersReducedMotion ? (
        <p className='text-lg sm:text-xl lg:text-2xl text-brand-text-secondary mb-6 md:mb-8 max-w-2xl mx-auto lg:mx-0 font-medium leading-relaxed'>
          LotteryFlow predicts your weekly lottery payment with{' '}
          <strong className='text-transparent bg-gradient-to-r from-emerald-600 to-cyan-600 bg-clip-text font-bold'>over 95% accuracy</strong>,
          so you can stop stressing about cash flow and focus on running your business.
        </p>
      ) : (
        <motion.p
          className='text-lg sm:text-xl lg:text-2xl text-brand-text-secondary mb-6 md:mb-8 max-w-2xl mx-auto lg:mx-0 font-medium leading-relaxed'
          variants={subtitleVariants}
          initial='initial'
          animate='animate'
        >
          LotteryFlow predicts your weekly lottery payment with{' '}
          <motion.strong 
            className='text-transparent bg-gradient-to-r from-emerald-600 to-cyan-600 bg-clip-text font-bold'
            animate={{
              backgroundPosition: ['0%', '100%', '0%'],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'linear',
            }}
            style={{
              backgroundSize: '200% 100%',
            }}
          >
            over 95% accuracy
          </motion.strong>,
          so you can stop stressing about cash flow and focus on running your business.
        </motion.p>
      )}

      {/* Social Proof Statement */}
      {prefersReducedMotion ? (
        <p className='text-sm lg:text-base text-brand-text-secondary mb-4 mx-auto lg:mx-0 font-medium opacity-90 italic'>
          "Trusted by retailers in the Illinois Lottery System."
        </p>
      ) : (
        <motion.p
          className='text-sm lg:text-base text-brand-text-secondary mb-4 mx-auto lg:mx-0 font-medium opacity-90 italic'
          variants={subtitleVariants}
          initial='initial'
          animate='animate'
          transition={{
            ...subtitleVariants.animate.transition,
            delay: subtitleVariants.animate.transition.delay + 0.1,
          }}
        >
          "Trusted by retailers in the Illinois Lottery System."
        </motion.p>
      )}

      {/*/!* Animated Sub-value proposition *!/*/}
      {/*{prefersReducedMotion ? (*/}
      {/*  <p className='hidden sm:block text-base lg:text-lg xl:text-xl text-brand-text-secondary mb-8 md:mb-10 mx-auto lg:mx-0 max-w-xl font-normal leading-relaxed'>*/}
      {/*    Join 500+ convenience store owners who've increased their lottery profits*/}
      {/*    by an average of 23% while reducing waste and improving cash flow management.*/}
      {/*  </p>*/}
      {/*) : (*/}
      {/*  <motion.p*/}
      {/*    className='hidden sm:block text-base lg:text-lg xl:text-xl text-brand-text-secondary mb-8 md:mb-10 mx-auto lg:mx-0 max-w-xl font-normal leading-relaxed'*/}
      {/*    variants={subtitleVariants}*/}
      {/*    initial='initial'*/}
      {/*    animate='animate'*/}
      {/*    transition={{*/}
      {/*      ...subtitleVariants.animate.transition,*/}
      {/*      delay: subtitleVariants.animate.transition.delay + 0.2,*/}
      {/*    }}*/}
      {/*  >*/}
      {/*    Join 500+ convenience store owners who've increased their lottery profits*/}
      {/*    by an average of 23% while reducing waste and improving cash flow management.*/}
      {/*  </motion.p>*/}
      {/*)}*/}

      {/* Animated CTA Buttons */}
      <div className='flex flex-col sm:flex-row gap-3 sm:gap-6 justify-center lg:justify-start mb-8 md:mb-0 md:mt-8'>
        <AnimatedButton
          variant='primary'
          size='lg'
          onClick={handleGetStartedClick}
          icon={<span>🚀</span>}
          className='w-full sm:w-auto min-w-[150px] relative'
          delay={HERO_ANIMATION_SEQUENCE.cta.delay}
        >
          {getPrimaryCTAText()}
          <span className='absolute -top-2 -right-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full animate-pulse'>
            Free Trial
          </span>
        </AnimatedButton>
        <AnimatedButton
          variant='outline'
          size='lg'
          onClick={handleWatchDemoClick}
          icon={<span>▶️</span>}
          className='w-full sm:w-auto min-w-[160px]'
          delay={HERO_ANIMATION_SEQUENCE.cta.delay + 0.1}
        >
          {getSecondaryCTAText()}
        </AnimatedButton>
      </div>

      {/* Animated Trust Indicators */}
      <motion.div
        className='mt-6 md:mt-8 flex flex-col sm:flex-row flex-wrap items-center justify-center lg:justify-start gap-3 sm:gap-4'
        variants={prefersReducedMotion ? {} : trustIndicatorsVariants}
        initial={prefersReducedMotion ? {} : 'initial'}
        animate={prefersReducedMotion ? {} : 'animate'}
      >
        <motion.div variants={prefersReducedMotion ? {} : staggerItem}>
          <TrustIndicator
            text='95%+ Accuracy'
            variant='success'
            animated={true}
            className='text-xs sm:text-sm'
          />
        </motion.div>
        <motion.div variants={prefersReducedMotion ? {} : staggerItem}>
          <TrustIndicator
            text='$2M+ Saved'
            variant='warning'
            animated={true}
            className='text-xs sm:text-sm'
          />
        </motion.div>
        <motion.div variants={prefersReducedMotion ? {} : staggerItem}>
          <TrustIndicator
            text='500+ Stores'
            variant='primary'
            animated={true}
            className='text-xs sm:text-sm'
          />
        </motion.div>
        {/*<motion.div variants={prefersReducedMotion ? {} : staggerItem}>*/}
        {/*  <TrustIndicator*/}
        {/*    text='3+ Years Proven'*/}
        {/*    variant='info'*/}
        {/*    animated={true}*/}
        {/*    className='text-xs sm:text-sm'*/}
        {/*  />*/}
        {/*</motion.div>*/}
      </motion.div>
    </ContainerComponent>
  );
};
